/*
 * HubSidebar — Prophets Hub desktop navigation.
 *
 * Renders as a fixed left column on tablet/desktop widths (≥1024 px).
 * On smaller viewports the component renders `null` and the existing
 * top-right hamburger menu (owned by HubHeader) handles navigation.
 *
 * The sidebar itself lives in a portal to `document.body` so no page
 * layout has to be rewritten — Hub pages continue to render as they
 * always have; a body-level padding rule (see HubSidebar.css) shifts
 * their content right by the sidebar width.
 *
 * Structure — nine flat, direct global destinations (no groups, no
 * dropdowns, no chevrons). Contextual navigation (e.g. Transmissions
 * platform filters, Marketplace subtabs, Profile subtabs) is owned by
 * each page's content area, NOT the sidebar.
 *
 * Behaviour:
 *   • Manually collapsed / expanded via a persistent button. State
 *     lives in localStorage ("hub.sidebar.collapsed") — never reset
 *     by page navigation or route changes.
 *   • Icons only when collapsed; each icon carries a `title` and an
 *     `aria-label` so tooltips + keyboard focus announce the label.
 *   • Middle nav region preserves its scrollTop across route changes
 *     (module-scoped so it survives per-page unmount/remount) so
 *     clicking a lower nav item like ZODIAC keeps that item visible
 *     after navigation. The scrollTop is only reset when the user
 *     toggles the collapse state (icon-only vs. label layout).
 *   • Reduced-motion preference disables the width transitions.
 *
 * This component does NOT own any hub data — it is a stateless nav
 * shell. Access guards, holder-tier checks, and authentication
 * behaviour continue to live on each destination page.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  Radar, Radio, Eye, Store, Gamepad2, Brain, Disc3, Trophy,
  User as UserIcon, Images, ChevronLeft, ChevronRight,
  Stars, Castle, Lock, Orbit, Zap, Coins, Spade,
} from "lucide-react";
import "./HubSidebar.css";
import { HUB_FEATURE_FLAGS } from "@/lib/hubFeatureFlags";
import { discordLoginUrl } from "@/lib/pendingInvite";

// ---- Persistence keys -------------------------------------------------
const LS_COLLAPSED = "hub.sidebar.collapsed";

// Module-scoped scrollTop. The sidebar is rendered inside each Hub
// page's HubHeader, so every route change unmounts and remounts this
// component. Persisting the last scrollTop on the module keeps the
// user's vertical position stable across route changes without any
// change to the app's mount tree.
let savedScrollTop = 0;

function readCollapsed() {
  try { return window.localStorage.getItem(LS_COLLAPSED) === "1"; }
  catch { return false; }
}
function writeCollapsed(v) {
  try { window.localStorage.setItem(LS_COLLAPSED, v ? "1" : "0"); }
  catch { /* localStorage unavailable — ephemeral is fine */ }
  // Notify HubHeader (which owns the body-level shell offset class)
  // so the main-content padding updates in the same frame as the
  // sidebar width, avoiding a shift-jank.
  try { window.dispatchEvent(new Event("hub-sidebar:toggle")); } catch { /* noop */ }
}

// ---- Structure --------------------------------------------------------
//
// Exactly nine flat, global destinations in the spec-mandated order.
// Every entry uses the same shared row component and geometry — HOME,
// TRANSMISSIONS, PREDICTIONS, GAMES, QUIZ, DAILY SPIN, MARKETPLACE,
// MY PORTFOLIO, PROFILE. No group state, no dropdowns, no children.
//
// Contextual subtabs (e.g. Transmissions platform filters, Marketplace
// Prize Vault / Community Raffles / My Activity, Profile Overview /
// Public Profile / Account, Quiz Current / History, Predictions
// tabs) live INSIDE each destination page — the sidebar simply
// receives the highlight signal via `activeKeys`.
//
// `accent` is a CSS colour surfaced via a `--acc` custom property; the
// row's hover glow, active tint and icon aura all read from `--acc` so
// each destination has its own energy signature without extra CSS.
export const HUB_NAV_ITEMS = [
  { key: "home",          label: "HOME",          to: "/hub",              icon: Radar,    accent: "var(--acc-home)" },
  { key: "transmissions", label: "TRANSMISSIONS", to: "/hub/transmissions", icon: Radio,    accent: "var(--acc-transmissions)",
    activeKeys: ["transmissions"] },
  { key: "predictions",   label: "PREDICTIONS",   to: "/hub/predictions",  icon: Eye,      accent: "var(--acc-predictions)",
    activeKeys: ["predictions", "my-predictions"] },
  { key: "games",         label: "GAMES",         to: "/hub/games",        icon: Gamepad2, accent: "var(--acc-games)" },
  // QUIZ — temporarily disabled via HUB_QUIZ_ENABLED. Backend/DB
  // untouched; flipping the flag restores this nav entry.
  ...(HUB_FEATURE_FLAGS.HUB_QUIZ_ENABLED
    ? [{ key: "quiz",     label: "QUIZ",          to: "/hub/quiz",         icon: Brain,    accent: "var(--acc-quiz)" }]
    : []),
  { key: "daily-spin",    label: "DAILY SPIN",    to: "/hub/daily-spin",   icon: Disc3,    accent: "var(--acc-daily-spin)" },
  { key: "marketplace",   label: "MARKETPLACE",   to: "/hub/marketplace",  icon: Store,    accent: "var(--acc-marketplace)" },
  { key: "orbis",         label: "ORBIS",         to: "/hub/orbis",        icon: Orbit,    accent: "var(--acc-orbis)" },
  { key: "portfolio",     label: "MY PORTFOLIO",  to: "/hub/portfolio",    icon: Images,   accent: "var(--acc-portfolio)" },
  { key: "profile",       label: "PROFILE",       to: "/hub/profile",      icon: UserIcon, accent: "var(--acc-profile)",
    activeKeys: ["profile", "profile-public", "profile-account", "profile-dashboard"] },
  { key: "family-race",   label: "FAMILY RACE",   to: "/hub/family-race",  icon: Trophy,   accent: "var(--acc-family-race)" },
  { key: "headquarters",  label: "HEADQUARTERS",  to: "/hub/headquarters", icon: Castle,   accent: "var(--acc-headquarters)",
    activeKeys: ["headquarters"] },
  // THE SWEEP sits directly between HEADQUARTERS and ZODIAC in
  // both desktop + mobile navs (single source of truth — this list
  // is consumed by every sidebar renderer).
  { key: "sweep",         label: "THE SWEEP",     to: "/hub/sweep",        icon: Zap,      accent: "var(--acc-orbis)" },
  { key: "zodiac",        label: "ZODIAC",        to: "/hub/zodiac",       icon: Stars,    accent: "var(--acc-zodiac)" },
  { key: "golden-vault",  label: "GOLDEN VAULT",  to: "/hub/golden-vault", icon: Lock,     accent: "var(--acc-golden-vault)" },
  // HOLD & EARN — passive NFT accrual. Kept last (no styling accent
  // yet — will inherit until final design).
  { key: "hold-and-earn", label: "HOLD & EARN",   to: "/hub/hold-and-earn", icon: Coins,    accent: "var(--acc-portfolio)" },
  // CASINO — Phase 1: Solana Pay single-ticket purchase. Temporarily
  // hidden via HUB_CASINO_ENABLED (unfinished). Page component, backend
  // routes, DB collections and admin tools are all preserved; flipping
  // the flag restores this nav entry on desktop and mobile at once.
  ...(HUB_FEATURE_FLAGS.HUB_CASINO_ENABLED
    ? [{ key: "casino",   label: "CASINO",        to: "/hub/casino",       icon: Spade,    accent: "var(--acc-portfolio)" }]
    : []),
];

// ---- Component --------------------------------------------------------
export default function HubSidebar({ active, me, onLogout }) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const navRef = useRef(null);
  useLocation();  // subscribe so active-state derives from URL on each render

  // Restore the last scrollTop when the sidebar first mounts (route
  // change unmounts and remounts this component; module-scoped
  // `savedScrollTop` persists the value across mounts). Uses
  // `useLayoutEffect` so the scroll position is restored BEFORE the
  // browser paints — no visible scroll jump.
  //
  // IMPORTANT — first-paint contract:
  //   Previously this component gated its own render behind a
  //   `mounted` state that flipped in `useEffect`. That gated the
  //   sidebar off for the very first paint after HubBootLoader
  //   unmounted, which — combined with the horizontal-tab flash in
  //   HubHeader — produced the "wrong layout" frame. There is no SSR
  //   in this app, so the gate was purely defensive; removing it
  //   makes the sidebar render on the very first paint.
  useLayoutEffect(() => {
    if (!navRef.current) return;
    navRef.current.scrollTop = savedScrollTop;
  });
  // NOTE: intentionally no dep array — every commit re-asserts the
  // saved scrollTop. This makes React StrictMode's double-invoke +
  // any accidental scroll reset caused by a parent re-render
  // idempotent: the layout effect always ends with the saved value
  // pinned back onto the nav element BEFORE paint. The `onScroll`
  // handler is the only writer of `savedScrollTop`, so this cannot
  // fight user input — a manual scroll updates `savedScrollTop`,
  // then the next commit pins that new value.

  // Persist scrollTop as the user scrolls so it survives the next
  // route-change remount.
  const onNavScroll = useCallback((e) => {
    savedScrollTop = e.currentTarget.scrollTop;
  }, []);

  const toggleCollapsed = useCallback(() => {
    // Compute the next state from localStorage (single source of
    // truth shared with PersistentSolRadio) rather than from local
    // `useState` — StrictMode double-invokes state updaters, so any
    // side effect (localStorage write, event dispatch) MUST live
    // outside the updater to avoid stale-state ping-pong.
    const next = !readCollapsed();
    writeCollapsed(next);
    setCollapsed(next);
    // A collapse toggle changes the visible row layout (icon-only
    // vs. label). Resetting the scroll to 0 here — imperatively,
    // inside the actual click handler — guarantees the reset ONLY
    // happens on a real user toggle. Route changes never touch this
    // path, so the sidebar scroll position is preserved across
    // navigation. (Previous implementation used a `useEffect`
    // guarded by a first-run ref, which React StrictMode's
    // double-invoke could still trigger on the first mount.)
    if (navRef.current) {
      navRef.current.scrollTop = 0;
    }
    savedScrollTop = 0;
  }, []);

  // Listen for external collapse changes broadcast by the persistent
  // SolRadio player (its compact icon writes to the same
  // `hub.sidebar.collapsed` key and dispatches `hub-sidebar:toggle`).
  // The event only fires from click handlers, never during render,
  // so it is safe to call setCollapsed here. We ALSO reset the
  // sidebar scroll to 0 to mirror the local toggle behaviour — the
  // event carries no route-change semantics, only visible-layout.
  useEffect(() => {
    const h = () => {
      setCollapsed(readCollapsed());
      if (navRef.current) navRef.current.scrollTop = 0;
      savedScrollTop = 0;
    };
    window.addEventListener("hub-sidebar:toggle", h);
    return () => window.removeEventListener("hub-sidebar:toggle", h);
  }, []);

  if (typeof document === "undefined") return null;

  const authed = !!(me && me.authenticated);
  const rootClass = `hub-sb ${collapsed ? "is-collapsed" : "is-expanded"}`
    + (` hub-sb-active-${active || "none"}`);

  const sidebar = (
    <aside className={rootClass}
           data-testid="hub-sidebar"
           data-collapsed={collapsed ? "1" : "0"}
           aria-label="Hub navigation">
      <div className="hub-sb__inner">
        <div className="hub-sb__topbar">
          {/* User pill — single-action:
              • logged-in  → open Profile page (logout moved to the
                             Profile page's ACCOUNT tab area).
              • logged-out → start Discord OAuth via the existing
                             canonical route. */}
          {authed ? (
            <Link to="/hub/profile"
                  className="hub-sb__user"
                  aria-label="Open profile"
                  title={collapsed ? "Profile" : undefined}
                  data-testid="hub-sidebar-user">
              <span className="hub-sb__user-avatar" aria-hidden="true">
                <img src={me?.user?.avatar_url} alt="" />
              </span>
              <span className="hub-sb__user-name">
                {me?.user?.display_name || me?.user?.username || "Prophet"}
              </span>
            </Link>
          ) : (
            <a href={discordLoginUrl()}
               className="hub-sb__user hub-sb__user--guest"
               aria-label="Log in with Discord"
               title={collapsed ? "Log in" : undefined}
               data-testid="hub-sidebar-user">
              <span className="hub-sb__user-avatar" aria-hidden="true">
                <UserIcon size={16} strokeWidth={1.8} />
              </span>
              <span className="hub-sb__user-name">CONNECT DISCORD</span>
            </a>
          )}
        </div>

        <nav ref={navRef}
             className="hub-sb__nav"
             onScroll={onNavScroll}
             aria-label="Hub sections">
          <ul className="hub-sb__list">
            {HUB_NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const accentStyle = item.accent ? { "--acc": item.accent } : undefined;
              // Active state derives ONLY from the URL-based `active`
              // prop. `activeKeys` allows one global entry to remain
              // active across its family of contextual routes
              // (e.g. PREDICTIONS active for both `predictions` and
              // `my-predictions`).
              const isActive = item.activeKeys
                ? item.activeKeys.includes(active)
                : active === item.key;
              return (
                <li key={item.key} className="hub-sb__item">
                  <Link to={item.to}
                        className={`hub-sb__row hub-sb__link${isActive ? " is-active" : ""}`}
                        aria-current={isActive ? "page" : undefined}
                        title={collapsed ? item.label : undefined}
                        style={accentStyle}
                        data-testid={`hub-sidebar-link-${item.key}`}>
                    <span className="hub-sb__icon" aria-hidden="true">
                      <Icon size={20} strokeWidth={1.7} />
                    </span>
                    <span className="hub-sb__label">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* --- Bottom: SolRadio placeholder --------------------------
            The SolRadio player itself lives in `<PersistentSolRadio>`
            (rendered ONCE at App level, outside <Routes>) so its
            iframe DOM node — and therefore its media session —
            survives every Hub route change. This div only reserves
            visual space at the bottom of the sidebar column so the
            page-rendered nav list does not overlap the fixed-position
            player. The persistent player also listens for the
            `hub-sidebar:toggle` window event so its visual width
            tracks the sidebar's collapsed state exactly. */}
        <div className="hub-sb__radio-spacer" aria-hidden="true" />
      </div>

      {/* Middle-edge collapse handle. Always visible in both states
          so the user can toggle from anywhere in the viewport. Sits
          half over the sidebar's right border at 50% vertical. */}
      <button type="button"
              className="hub-sb__collapse-handle"
              onClick={toggleCollapsed}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-pressed={collapsed ? "true" : "false"}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              data-testid="hub-sidebar-collapse-toggle">
        {collapsed
          ? <ChevronRight size={16} strokeWidth={2.2} aria-hidden="true" />
          : <ChevronLeft size={16} strokeWidth={2.2} aria-hidden="true" />}
      </button>
    </aside>
  );

  // Portal to <body> so no ancestor transform/filter can constrain a
  // position:fixed sidebar.
  return createPortal(sidebar, document.body);
}
